import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useScrollLock } from '../hooks/useScrollLock'

interface SheetProps {
	open: boolean
	onClose: () => void
	variant?: 'bottom' | 'center'
	title?: string
	showHandle?: boolean
	className?: string
	contentClassName?: string
	children: React.ReactNode
	/** Кастомные элементы между title и close-крестиком в header'е —
	 *  например help-кнопка `?` для перезапуска тура, счётчик и т.п. */
	headerExtra?: React.ReactNode
}

const CLOSE_DURATION = 300

// Стек открытых шитов: Escape закрывает только ВЕРХНИЙ (последний открытый),
// чтобы вложенный шит не сносил родителя одним нажатием.
const sheetStack: symbol[] = []

export default function Sheet({
	open,
	onClose,
	variant = 'bottom',
	title,
	showHandle,
	className,
	contentClassName,
	children,
	headerExtra,
}: SheetProps) {
	const [visible, setVisible] = useState(false)
	const [closing, setClosing] = useState(false)
	const closingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const idRef = useRef<symbol | null>(null)
	if (!idRef.current) idRef.current = Symbol('sheet')

	// Показываем/скрываем с анимацией
	useEffect(() => {
		if (open) {
			if (closingTimerRef.current) {
				clearTimeout(closingTimerRef.current)
				closingTimerRef.current = null
			}
			setClosing(false)
			setVisible(true)
		} else if (visible && !closing) {
			// open стал false снаружи — запускаем анимацию закрытия
			setClosing(true)
			closingTimerRef.current = setTimeout(() => {
				setClosing(false)
				setVisible(false)
				closingTimerRef.current = null
			}, CLOSE_DURATION)
		}
	}, [open]) // eslint-disable-line react-hooks/exhaustive-deps

	// Cleanup таймера при unmount
	useEffect(() => {
		return () => {
			if (closingTimerRef.current) clearTimeout(closingTimerRef.current)
		}
	}, [])

	// Scroll lock с сохранением позиции (общий хук; refcount для вложенных модалок)
	useScrollLock(visible)

	// Регистрируем шит в стеке, пока он видим (для Escape-приоритета).
	useEffect(() => {
		if (!visible) return
		const id = idRef.current as symbol
		sheetStack.push(id)
		return () => {
			const i = sheetStack.indexOf(id)
			if (i >= 0) sheetStack.splice(i, 1)
		}
	}, [visible])

	// Escape — реагирует только верхний (последний открытый) шит.
	useEffect(() => {
		if (!visible || closing) return
		const handler = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return
			if (sheetStack[sheetStack.length - 1] !== idRef.current) return
			onClose()
		}
		document.addEventListener('keydown', handler)
		return () => document.removeEventListener('keydown', handler)
	}, [visible, closing, onClose])

	if (!visible) return null

	const sheetClass = [
		'sheet',
		`sheet--${variant}`,
		closing && 'sheet--closing',
		className,
	].filter(Boolean).join(' ')

	return createPortal(
		<div
			className={`sheet-backdrop${closing ? ' sheet-backdrop--closing' : ''}`}
			onClick={onClose}
		>
			<div
				className={sheetClass}
				onClick={(e) => e.stopPropagation()}
			>
				{showHandle && <div className="sheet__handle" />}

				{title && (
					<div className="sheet__header">
						<span className="sheet__title">{title}</span>
						{headerExtra}
						<button className="sheet__close" onClick={onClose} type="button">
							<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
								<line x1="18" y1="6" x2="6" y2="18" />
								<line x1="6" y1="6" x2="18" y2="18" />
							</svg>
						</button>
					</div>
				)}

				<div className={`sheet__content${contentClassName ? ` ${contentClassName}` : ''}`}>
					{children}
				</div>
			</div>
		</div>,
		document.body,
	)
}
